import {
  AccordionSummary,
  Accordion,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Checkbox,
  ListItemText,
  Box,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useMemo, useState } from 'react';
import { useFilterChannelOrEvent } from '../../../../../../redux/monitoring/hooks/useFilterChannelOrEvent';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  CommanderMonitoringFilterLogicalObject,
  getNumberOfChannels,
  KEYS_CONTROL_UNITS_BY_TYPE,
} from '../../../../../../redux/monitoring/api/util/filter/filter.model';
import {
  getPolyglot,
  PolyglotComponentProps,
  withPolyglot,
} from '../../../../../../i18n';
import { ZipDeviceControlUnit } from '../../../../../../redux/controlUnit/controlUnit.model';
import { STModal } from '../../../../../commons/Modal';
import { useAppSelector } from '../../../../../../redux/store.model';
import { getCUsTranslationsSelector } from '../../../../../../redux/CUTranslation/selectors';
import { DeviceControlUnitsEventsMetadataAPIResponse } from '../../../../../../redux/commander/api/device.model';
import { isNotNullOrUndefined } from '../../../../../../util/types/Array';
import { getTranslationNameByData } from '../../../../../../redux/monitoring/api/util/getLabelName';
import useStateBoolean from '../../../../../../util/hooks/useStateBoolean';
import {
  CHANNELS_CONNECTIONS_SUPPORTED,
  CONNECTION_INFO_CU_KEY,
} from './useLabStateFilters';
import { useConnectionControls } from './useConnectionControls';

export interface ConnectionInfoFiltersModalButtonProps {
  devicesControlUnits: ZipDeviceControlUnit[];
  value: CommanderMonitoringFilterLogicalObject;
  onSave: (value: CommanderMonitoringFilterLogicalObject) => unknown;
}
export const ConnectionInfoFiltersModalButton = (
  props: ConnectionInfoFiltersModalButtonProps
) => {
  const { devicesControlUnits, value, onSave } = props;
  const polyglot = getPolyglot();
  const type = 'labConnection';
  const [showConnectionModal, openConnectionModal, closeConnectionModal] =
    useStateBoolean(false);
  const controls = useConnectionControls(type, devicesControlUnits, value);
  const title = polyglot.t('lab.connection_feature');

  const openModal = () => {
    controls.reset();
    openConnectionModal();
  };

  const save = () => {
    // onSave is expected to be a function that saves the filter
    onSave(controls.filterLogicalObject);
    closeConnectionModal();
  };
  const numberOfChannels = useMemo(
    () => getNumberOfChannels(value ?? {}),
    [value]
  );
  const selected = ` (${numberOfChannels.active}/${numberOfChannels.total})`;
  return (
    <>
      <Button
        onClick={openModal}
        startIcon={<FilterListIcon />}
        variant="outlined"
      >
        {title + selected}
      </Button>
      <STModal
        open={showConnectionModal}
        onClose={closeConnectionModal}
        title={title}
        fullWidth
        maxWidth="md"
        buttonActions={
          <Button onClick={save} color="primary">
            {polyglot.t('button.save')}
          </Button>
        }
      >
        <ConnectionInfoFiltersView {...props} controls={controls} type={type} />
      </STModal>
    </>
  );
};

export interface ConnectionInfoFiltersViewProps {
  devicesControlUnits: ZipDeviceControlUnit[];
  type: 'labConnection';
  controls: ReturnType<typeof useFilterChannelOrEvent>;
}
export const ConnectionInfoFiltersView = (
  props: ConnectionInfoFiltersViewProps
) => {
  const { devicesControlUnits, controls } = props;
  const { toggleItem, toggleDevice, getStateItem, getStateDevice } = controls;

  return (
    <Box className="ConnectionInfoFilters" sx={{ width: '100%' }}>
      {devicesControlUnits.map((zipDeviceControlUnit) => (
        <Accordion
          key={zipDeviceControlUnit.deviceId}
          id={`device-title-${zipDeviceControlUnit.deviceId}`}
          aria-controls={`device-title-${zipDeviceControlUnit.deviceId}`}
          className={`ConnectionInfoFilters__Device Device-${zipDeviceControlUnit.deviceId}`}
          defaultExpanded={true}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Checkbox
              id={`device-checkbox-${zipDeviceControlUnit.deviceId}`}
              inputProps={
                {
                  'data-testid': `device-checkbox-${zipDeviceControlUnit.deviceId}`,
                } as any
              }
              edge="start"
              checked={getStateDevice(zipDeviceControlUnit) === 'checked'}
              onClick={(e) => {
                e.stopPropagation();
                toggleDevice(zipDeviceControlUnit);
              }}
              indeterminate={
                getStateDevice(zipDeviceControlUnit) === 'indeterminate'
              }
              tabIndex={-1}
            />
            <Typography variant="h5" display="flex" alignItems="center">
              {zipDeviceControlUnit.deviceName}
            </Typography>
          </AccordionSummary>
          <Box sx={{ padding: 1 }}>
            <List>
              {CHANNELS_CONNECTIONS_SUPPORTED.map((eventId) => {
                const primaryText = eventId;

                return (
                  <ListItem
                    key={eventId}
                    className={`ConnectionInfoFilters__Event Event-${eventId}`}
                    disablePadding
                  >
                    <ListItemButton
                      role={undefined}
                      onClick={() =>
                        toggleItem(
                          zipDeviceControlUnit.deviceId,
                          CONNECTION_INFO_CU_KEY,
                          eventId
                        )
                      }
                      dense
                    >
                      <ListItemIcon>
                        <Checkbox
                          id={`event-checkbox-${zipDeviceControlUnit.deviceId}-${CONNECTION_INFO_CU_KEY}-${eventId}`}
                          edge="start"
                          checked={
                            getStateItem(
                              zipDeviceControlUnit.deviceId,
                              CONNECTION_INFO_CU_KEY,
                              eventId
                            ) === 'checked'
                          }
                          indeterminate={
                            getStateItem(
                              zipDeviceControlUnit.deviceId,
                              CONNECTION_INFO_CU_KEY,
                              eventId
                            ) === 'indeterminate'
                          }
                          tabIndex={-1}
                          disableRipple
                          inputProps={
                            {
                              'aria-labelledby': `event-title-${zipDeviceControlUnit.deviceId}-${CONNECTION_INFO_CU_KEY}-${eventId}-label`,
                              'data-testid': `event-checkbox-${zipDeviceControlUnit.deviceId}-${CONNECTION_INFO_CU_KEY}-${eventId}`,
                            } as any
                          }
                        />
                      </ListItemIcon>
                      <ListItemText
                        id={`event-title-${zipDeviceControlUnit.deviceId}-${CONNECTION_INFO_CU_KEY}-${eventId}-label`}
                        primary={primaryText}
                        primaryTypographyProps={{
                          sx: { wordBreak: 'break-all' },
                        }}
                        secondaryTypographyProps={{
                          sx: { wordBreak: 'break-all' },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Accordion>
      ))}
    </Box>
  );
};

export default ConnectionInfoFiltersView;
